import React, { useState, useEffect } from "react";
import { styled } from "@material-ui/styles";
import { IStyledArguments } from "../../types/styled-arguments";
import {
  Box,
  Card,
  Grid,
  Tabs,
  Tab,
  Typography as MuiTypography,
  Avatar,
} from "@material-ui/core";
import { RouteComponentProps } from "@reach/router";
import { useQuery } from "@apollo/client";
import Typography from "../typography";
import { WORKSPACE_MANAGEMENT_DATA } from "./WorkspaceManagementQueries";
import { IWorkspaceDetails, IWorkspaceUser, IPlayerWithUsers, ICoachUser } from "../../types";
import PlayersManagement from "./PlayersManagement";
import CoachesManagement from "./CoachesManagement";
import UsersManagement from "./UsersManagement";
import PeopleIcon from "@material-ui/icons/People";
import PersonIcon from "@material-ui/icons/Person";
import SportsIcon from "@material-ui/icons/Sports";
import GroupIcon from "@material-ui/icons/Group";
import BusinessIcon from "@material-ui/icons/Business";

// Styled Components
const PageContainer = styled("div")(({ theme }: IStyledArguments) => ({
  background: theme.palette.extra.backgroundGrey,
  minHeight: "100vh",
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

const ContentWrapper = styled("div")({
  maxWidth: "1400px",
  margin: "0 auto",
});

const HeaderCard = styled(Card)(({ theme }: IStyledArguments) => ({
  background: theme.palette.primary.main,
  borderRadius: "18px",
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  color: "white",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3),
  },
}));

const StatsGrid = styled(Grid)(({ theme }: IStyledArguments) => ({
  marginBottom: theme.spacing(3),
}));

const StatCard = styled(Card)(({ theme }: IStyledArguments) => ({
  borderRadius: "16px",
  padding: theme.spacing(3),
  height: "100%",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
  transition: "transform 0.2s ease, box-shadow 0.2s ease",
  "&:hover": {
    transform: "translateY(-4px)",
    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
  },
}));

const StatIconWrapper = styled(Box)(({ theme }: IStyledArguments) => ({
  width: 60,
  height: 60,
  borderRadius: "12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: theme.palette.primary.main,
  "& svg": {
    fontSize: "2rem",
    color: "white",
  },
}));

const StatContent = styled(Box)({
  flex: 1,
});

const StyledTabs = styled(Tabs)(({ theme }: IStyledArguments) => ({
  marginBottom: theme.spacing(3),
  "& .MuiTab-root": {
    textTransform: "none",
    fontSize: "1rem",
    fontWeight: 600,
    minWidth: 120,
  },
  "& .MuiTabs-indicator": {
    height: 3,
    borderRadius: "3px 3px 0 0",
  },
}));

const TabPanel = styled(Box)({
  paddingTop: "24px",
  paddingBottom: "48px",
});

interface IPageWorkspaceManagement extends RouteComponentProps {
  workspaceId?: string;
}

const PageWorkspaceManagement: React.FC<IPageWorkspaceManagement> = ({
  workspaceId,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [workspaceDetails, setWorkspaceDetails] = useState<IWorkspaceDetails | null>(null);
  const [users, setUsers] = useState<IWorkspaceUser[]>([]);
  const [players, setPlayers] = useState<IPlayerWithUsers[]>([]);
  const [coaches, setCoaches] = useState<ICoachUser[]>([]);

  const { data, loading, error, refetch } = useQuery(WORKSPACE_MANAGEMENT_DATA, {
    variables: { workspaceId },
    skip: !workspaceId,
  });

  useEffect(() => {
    if (data) {
      setWorkspaceDetails(data.workspaceDetails);
      setUsers(data.workspaceUsers || []);
      setPlayers(data.playersWithUsers || []);
      setCoaches(data.workspaceCoaches || []);
    }
  }, [data]);

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <MuiTypography variant="h5">Loading workspace data...</MuiTypography>
        </Box>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Typography.H5 style={{ color: "red" }}>
            Error loading workspace data: {error.message}
          </Typography.H5>
        </ContentWrapper>
      </PageContainer>
    );
  }

  if (!workspaceDetails) {
    return (
      <PageContainer>
        <ContentWrapper>
          <Typography.H5>No workspace data available</Typography.H5>
        </ContentWrapper>
      </PageContainer>
    );
  }

  const stats = workspaceDetails.stats;

  return (
    <PageContainer>
      <ContentWrapper>
        {/* Header Section */}
        <HeaderCard>
          <Box display="flex" alignItems="center" mb={2} style={{ gap: 16 }}>
            <Avatar
              style={{
                width: 60,
                height: 60,
                background: "rgba(255, 255, 255, 0.2)",
              }}
            >
              <BusinessIcon style={{ fontSize: "2rem" }} />
            </Avatar>
            <Box>
              <Typography.H4 style={{ color: "white", margin: 0 }}>
                Workspace Management
              </Typography.H4>
              <MuiTypography variant="subtitle1" style={{ color: "rgba(255, 255, 255, 0.9)" }}>
                Owner: {workspaceDetails.owner.name} {workspaceDetails.owner.surname}
              </MuiTypography>
              {workspaceDetails.description && (
                <MuiTypography variant="body2" style={{ color: "rgba(255, 255, 255, 0.8)" }}>
                  {workspaceDetails.description}
                </MuiTypography>
              )}
            </Box>
          </Box>
        </HeaderCard>

        {/* Statistics Cards */}
        <StatsGrid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatIconWrapper>
                <PeopleIcon />
              </StatIconWrapper>
              <StatContent>
                <MuiTypography variant="h4" style={{ fontWeight: 700, margin: 0 }}>
                  {stats.totalUsers}
                </MuiTypography>
                <MuiTypography variant="body2" color="textSecondary">
                  Total Users
                </MuiTypography>
              </StatContent>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatIconWrapper>
                <PersonIcon />
              </StatIconWrapper>
              <StatContent>
                <MuiTypography variant="h4" style={{ fontWeight: 700, margin: 0 }}>
                  {stats.totalPlayers}
                </MuiTypography>
                <MuiTypography variant="body2" color="textSecondary">
                  Total Players
                </MuiTypography>
              </StatContent>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatIconWrapper>
                <SportsIcon />
              </StatIconWrapper>
              <StatContent>
                <MuiTypography variant="h4" style={{ fontWeight: 700, margin: 0 }}>
                  {stats.totalCoaches}
                </MuiTypography>
                <MuiTypography variant="body2" color="textSecondary">
                  Total Coaches
                </MuiTypography>
              </StatContent>
            </StatCard>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <StatIconWrapper>
                <GroupIcon />
              </StatIconWrapper>
              <StatContent>
                <MuiTypography variant="h4" style={{ fontWeight: 700, margin: 0 }}>
                  {stats.totalTeams}
                </MuiTypography>
                <MuiTypography variant="body2" color="textSecondary">
                  Total Teams
                </MuiTypography>
              </StatContent>
            </StatCard>
          </Grid>
        </StatsGrid>

        {/* Tabs Navigation */}
        <Card style={{ borderRadius: "16px", overflow: "hidden" }}>
          <StyledTabs
            value={activeTab}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
          >
            <Tab label="Players" icon={<PersonIcon />} />
            <Tab label="Users" icon={<PeopleIcon />} />
            <Tab label="Coaches" icon={<SportsIcon />} />
          </StyledTabs>

          {/* Tab Panels */}
          {activeTab === 0 && (
            <TabPanel>
              <PlayersManagement
                players={players}
                users={users}
                workspaceId={workspaceId || ""}
                onRefresh={refetch}
              />
            </TabPanel>
          )}

          {activeTab === 1 && (
            <TabPanel>
              <UsersManagement
                users={users}
                coaches={coaches}
                workspaceId={workspaceId || ""}
                onRefresh={refetch}
              />
            </TabPanel>
          )}

          {activeTab === 2 && (
            <TabPanel>
              <CoachesManagement
                coaches={coaches}
                workspaceId={workspaceId || ""}
                onRefresh={refetch}
              />
            </TabPanel>
          )}
        </Card>
      </ContentWrapper>
    </PageContainer>
  );
};

export default PageWorkspaceManagement;
